/**
 * Position requests — following them, and finding a vacant position to use
 * instead of creating a new one.
 *
 * identity-service /org/position-requests (routers/position_requests.py).
 * Callers: HR Ops' Position requests page and New position dialog, and Role
 * Master's Requests tab. Audience: admin, hr_admin, fno_admin.
 */
import { apiClient } from '../apiClient';
import type { SelectionApproach } from './hireRequestService';

const BASE_URL = '/identity/org/position-requests';

/** A hiring manager's business case, as the request keeps it under `hire`. */
export interface HireBusinessCase {
  /** Why the hire is needed, as the manager wrote it. */
  business_case?: string | null;
  selection_approach?: SelectionApproach | null;
}

/** Where a request has got to. A hiring manager's request that reserved an
 *  existing vacancy is `position_reserved` while the vacancy has budget for
 *  the start year, `reserved_no_budget` while it has none. */
export type PositionRequestStage =
  | 'awaiting_funding'
  | 'awaiting_approval'
  | 'creating'
  | 'needs_attention'
  | 'created'
  | 'position_reserved'
  | 'reserved_no_budget'
  | 'filled'
  | 'rejected'
  | 'withdrawn';

/** A vacant position that already matches an ask. */
export interface ExistingSeat {
  fo_position_id: string;
  title?: string | null;
  job_id?: string | null;
  /** Found by the same job, or only by the same role title. */
  matched_on: 'job' | 'title';
  /** Same role title, but the position is coded to another job. */
  job_differs: boolean;
  position_type_id?: string | null;
  campus?: string | null;
  department?: string | null;
  faculty?: string | null;
  match_tier: number;
  dims_match: boolean;
  faculty_state: string;
  /** exact: same job, campus, department and faculty. near: same campus and
   *  department, but another faculty or job. elsewhere: another campus or
   *  department. */
  strength: 'exact' | 'near' | 'elsewhere';
  reports_to?: string | null;
  reports_to_manager?: string | null;
  /** null when the ask named no manager. */
  reports_to_match: boolean | null;
  funded: boolean;
  budgeted_annual: number;
  held: boolean;
  hold_reason?: string | null;
  /** Created by another request, whose hire may still be coming, or reserved
   *  by a hiring manager's request (`kind`). */
  reserved_for?: {
    kind?: 'created' | 'reserved';
    request_number: string;
    requested_by?: string | null;
    created_on?: string | null;
    worker_name?: string | null;
    draft_id?: number | null;
  } | null;
  vacant_since?: string | null;
}

export interface ExistingSeatsResult {
  /** The closest few of each kind (or every one, when asked with `full`). */
  candidates: ExistingSeat[];
  /** The real totals, which can be more than `candidates` holds. */
  counts: { exact: number; near: number; elsewhere: number; total: number };
  /** Same-role positions at the asked campus and department that aren't
   *  offered: another position type, or someone already assigned in F&O. */
  excluded: { other_type: number; legacy_type: number; unknown_type: number; assigned?: number };
  /** An exact, unclaimed match exists: say why a new position is needed. */
  needs_reason: boolean;
  /** The ask as matched, with the campus and department codes normalised;
   *  enough to ask again for the full list. */
  asked?: {
    job_id?: string | null;
    title?: string | null;
    position_type_id?: string | null;
    campus?: string | null;
    department?: string | null;
    faculty?: string | null;
    reports_to?: string | null;
    year?: number | null;
    exclude?: string[];
  } | null;
}

export interface ExistingSeatAsk {
  key?: string;
  job_id?: string | null;
  title?: string | null;
  /** The F&O PositionTypeId the new position would have, e.g. "FT Regular". */
  position_type_id?: string | null;
  campus?: string | null;
  department?: string | null;
  faculty?: string | null;
  reports_to_position_id?: string | null;
  line_manager_pn?: string | null;
  start_date?: string | null;
  /** Positions never to offer, e.g. the request's own created ones. */
  exclude?: string[];
  /** Every matching position, not the closest few. One ask per call. */
  full?: boolean;
}

export interface PositionRequestOrigin {
  /** `manager`: raised by the hiring manager themselves. */
  channel: 'hr_ops' | 'bulk_hire' | 'budget_workbench' | 'role_master' | 'manager';
  context?: 'adhoc' | 'convert' | 'transfer' | 'reinstate';
  worker_pn?: string;
  worker_name?: string;
  draft_id?: number;
  rows?: number[];
  new_seat_reason?: string;
  existing_seats_seen?: string[];
}

export interface TrackedRequestGroup {
  index: number;
  job_id?: string | null;
  title?: string | null;
  count: number;
  position_type_id?: string | null;
  campus?: string | null;
  department?: string | null;
  faculty?: string | null;
  reports_to_position_id?: string | null;
  reports_to_manager?: string | null;
  start_date?: string | null;
  /** Open requests only. */
  existing_seats?: ExistingSeatsResult | null;
}

export interface TrackedPositionRequest {
  id: number;
  request_number: string;
  title: string;
  status: string;
  stage: PositionRequestStage;
  /** Only on a hiring manager's request. `existing`: the vacancy it holds,
   *  whether that has budget, and the reporting line HR Ops moves it to when
   *  it sits under someone else today. `new`: the appointment asked for.
   *  Either carries the business case once the manager's page has sent one:
   *  why the hire is needed, and the selection approach. */
  hire?:
    | ({
        mode: 'existing';
        position_id: string;
        funded: boolean;
        reports_to?: string | null;
        current_reports_to?: string | null;
        contract_end?: string | null;
        /** The appointment: Permanent, FTC, ICA or Intern. */
        category?: string | null;
      } & HireBusinessCase)
    | ({ mode: 'new'; category?: string | null; contract_end?: string | null } & HireBusinessCase)
    | null;
  requested_at?: string | null;
  updated_at?: string | null;
  requested_by?: { name?: string | null; email?: string | null; is_me?: boolean } | null;
  origin: PositionRequestOrigin;
  justification?: string | null;
  groups: TrackedRequestGroup[];
  count: number;
  start_date?: string | null;
  end_date?: string | null;
  funding: {
    state: 'none' | 'partial' | 'funded' | 'unbudgeted' | 'mixed';
    resolved: number;
    positions: number;
  };
  decision?: {
    action: 'approved' | 'rejected';
    reason?: string | null;
    use_instead_position_id?: string | null;
    by?: string | null;
    at?: string | null;
  } | null;
  withdrawal?: { reason?: string | null; by?: string | null; at?: string | null } | null;
  provision: { created: number; failed: number; error?: string | null };
  positions: Array<{
    fo_position_id: string;
    filled: boolean;
    incumbent_name?: string | null;
    incumbent_pn?: string | null;
  }>;
  can_withdraw: boolean;
}

export interface TrackedPositionRequestPage {
  items: TrackedPositionRequest[];
  has_more: boolean;
  counts: { open: number; closed: number };
  /** When the positions list was last refreshed from F&O. */
  data_as_of?: string | null;
}

export const positionRequestService = {
  async list(params: {
    scope?: 'mine' | 'all';
    state?: 'open' | 'closed' | 'all';
    q?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<TrackedPositionRequestPage> {
    const response = await apiClient.get<TrackedPositionRequestPage>(BASE_URL, { params });
    return response.data;
  },

  /** Vacant positions that already match each ask, keyed by the ask's key. */
  async existingSeats(asks: ExistingSeatAsk[]): Promise<{
    results: Record<string, ExistingSeatsResult>;
    data_as_of?: string | null;
  }> {
    const response = await apiClient.post<{
      results: Record<string, ExistingSeatsResult>;
      data_as_of?: string | null;
    }>(`${BASE_URL}/existing-seats`, { asks });
    return response.data;
  },

  async withdraw(requestId: number, reason: string): Promise<{
    ok: boolean;
    request_number: string;
    status: string;
  }> {
    const response = await apiClient.post<{ ok: boolean; request_number: string; status: string }>(
      `${BASE_URL}/${requestId}/withdraw`, { reason });
    return response.data;
  },
};
